import * as api from "./api/index";
import * as endpoints from "./api/endpoints";

const state = () => ({
  entities: [],
  recordsByCode: {},
  entitiesLoaded: false,
  receptionQueue: [],
  receptionQueuePagination: null,
  receptionMappings: [],
  receptionMappingsPagination: null,
  receptionSettings: {},
  receptionSelectedItem: null,
  receptionPreview: null,
  transferQueue: [],
  transferQueuePagination: null,
  transferMappings: [],
  transferMappingsPagination: null,
  transferSettings: {},
  transferSelectedItem: null,
  transferPreview: null,
  consumQueue: [],
  consumQueuePagination: null,
  consumSelectedItem: null,
});

const mutations = {
  SET_ERP_ENTITIES(currentState, entities) {
    currentState.entities = Array.isArray(entities) ? entities : [];
    currentState.entitiesLoaded = true;
  },
  UPSERT_ERP_ENTITY(currentState, entity) {
    if (!entity || !entity.code) {
      return;
    }

    const normalizedCode = entity.code.toUpperCase();
    const index = currentState.entities.findIndex(
      (item) => (item?.code || "").toUpperCase() === normalizedCode
    );

    if (index >= 0) {
      currentState.entities.splice(index, 1, entity);
      return;
    }

    currentState.entities.push(entity);
  },
  SET_ERP_ENTITY_RECORDS(currentState, { code, records }) {
    if (!code) {
      return;
    }

    currentState.recordsByCode = {
      ...currentState.recordsByCode,
      [code.toUpperCase()]: Array.isArray(records) ? records : [],
    };
  },
  SET_RECEPTION_QUEUE(currentState, payload) {
    currentState.receptionQueue = Array.isArray(payload?.data) ? payload.data : [];
    currentState.receptionQueuePagination = payload?.pagination || null;
  },
  SET_RECEPTION_QUEUE_ITEM(currentState, item) {
    currentState.receptionSelectedItem = item || null;
  },
  SET_RECEPTION_MAPPINGS(currentState, payload) {
    currentState.receptionMappings = Array.isArray(payload?.data) ? payload.data : [];
    currentState.receptionMappingsPagination = payload?.pagination || null;
  },
  SET_RECEPTION_SETTINGS(currentState, settings) {
    currentState.receptionSettings = settings && typeof settings === "object" ? settings : {};
  },
  SET_RECEPTION_PREVIEW(currentState, preview) {
    currentState.receptionPreview = preview || null;
  },
  SET_TRANSFER_QUEUE(currentState, payload) {
    currentState.transferQueue = Array.isArray(payload?.data) ? payload.data : [];
    currentState.transferQueuePagination = payload?.pagination || null;
  },
  SET_TRANSFER_QUEUE_ITEM(currentState, item) {
    currentState.transferSelectedItem = item || null;
  },
  SET_TRANSFER_MAPPINGS(currentState, payload) {
    currentState.transferMappings = Array.isArray(payload?.data) ? payload.data : [];
    currentState.transferMappingsPagination = payload?.pagination || null;
  },
  SET_TRANSFER_SETTINGS(currentState, settings) {
    currentState.transferSettings = settings && typeof settings === "object" ? settings : {};
  },
  SET_TRANSFER_PREVIEW(currentState, preview) {
    currentState.transferPreview = preview || null;
  },
  SET_CONSUM_QUEUE(currentState, payload) {
    currentState.consumQueue = Array.isArray(payload?.data) ? payload.data : [];
    currentState.consumQueuePagination = payload?.pagination || null;
  },
  SET_CONSUM_QUEUE_ITEM(currentState, item) {
    currentState.consumSelectedItem = item || null;
  },
};

const getters = {
  erpEntities: (currentState) => currentState.entities,
  erpEntitiesLoaded: (currentState) => currentState.entitiesLoaded,
  erpEntityRecords: (currentState) => (code) => {
    if (!code) {
      return [];
    }

    return currentState.recordsByCode[code.toUpperCase()] || [];
  },
  receptionQueue: (currentState) => currentState.receptionQueue,
  receptionQueuePagination: (currentState) => currentState.receptionQueuePagination,
  receptionSelectedItem: (currentState) => currentState.receptionSelectedItem,
  receptionMappings: (currentState) => currentState.receptionMappings,
  receptionMappingsPagination: (currentState) => currentState.receptionMappingsPagination,
  receptionSettings: (currentState) => currentState.receptionSettings,
  receptionPreview: (currentState) => currentState.receptionPreview,
  transferQueue: (currentState) => currentState.transferQueue,
  transferQueuePagination: (currentState) => currentState.transferQueuePagination,
  transferSelectedItem: (currentState) => currentState.transferSelectedItem,
  transferMappings: (currentState) => currentState.transferMappings,
  transferMappingsPagination: (currentState) => currentState.transferMappingsPagination,
  transferSettings: (currentState) => currentState.transferSettings,
  transferPreview: (currentState) => currentState.transferPreview,
  consumQueue: (currentState) => currentState.consumQueue,
  consumQueuePagination: (currentState) => currentState.consumQueuePagination,
  consumSelectedItem: (currentState) => currentState.consumSelectedItem,
};

function unwrapResponseData(response) {
  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data?.data)) {
    return response.data.data.data;
  }

  return [];
}

function unwrapResponseObject(response) {
  if (response?.data?.data && !Array.isArray(response.data.data)) {
    return response.data.data;
  }

  if (response?.data && !Array.isArray(response.data)) {
    return response.data;
  }

  return null;
}

function unwrapEnvelope(response) {
  if (response?.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }

  if (response && typeof response === "object" && !Array.isArray(response)) {
    return response;
  }

  return {};
}

const actions = {
  fetchErpEntities({ commit }) {
    return api.fetchFull(endpoints.ERP_ENTITIES, {}, commit, () => {}).then((response) => {
      const entities = unwrapResponseData(response);
      commit("SET_ERP_ENTITIES", entities);
      return entities;
    });
  },
  fetchErpEntityRecords({ commit }, payload) {
    const code = typeof payload === "string" ? payload : payload?.code;
    const params = typeof payload === "object" && payload ? (payload.params || { ...payload, code: undefined }) : { limit: 100 };
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_RECORDS, { entityCode: code });

    return api.fetchFull(url, params, commit, () => {}).then((response) => {
      const records = Array.isArray(response?.data?.data?.data)
        ? response.data.data.data
        : (Array.isArray(response?.data?.data) ? response.data.data : []);
      commit("SET_ERP_ENTITY_RECORDS", { code, records });
      return records;
    });
  },
  updateErpEntity({ commit }, { code, payload }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_BY_CODE, { entityCode: code });

    return api.put(url, payload, commit, () => {}).then((response) => {
      const entity = unwrapResponseObject(response);

      if (entity) {
        commit("UPSERT_ERP_ENTITY", entity);
      }

      return entity;
    });
  },
  detectErpEntitySchema({ commit }, code) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_DETECT_SCHEMA, { entityCode: code });

    return api.post(url, {}, commit, () => {}).then((response) => {
      const entity = unwrapResponseObject(response);

      if (entity) {
        commit("UPSERT_ERP_ENTITY", entity);
      }

      return entity;
    });
  },
  syncErpEntity({ commit }, code) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_SYNC, { entityCode: code });

    return api.post(url, {}, commit, () => {}).then((response) => {
      if (response?.data && !Array.isArray(response.data)) {
        return response.data;
      }

      return response?.data?.data || {};
    });
  },
  fetchErpSyncLog({ commit }, uuid) {
    const url = endpoints.replaceParams(endpoints.ERP_SYNC_LOG_BY_UUID, { uuid });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => {
      return unwrapEnvelope(response?.data || response);
    });
  },
  fetchApprovedConsums({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_APPROVED_CONSUMS, filters, commit, () => {}).then((response) => {
      return response?.data || { data: [], pagination: null };
    });
  },
  fetchApprovedConsumBatches({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_APPROVED_CONSUM_BATCHES, filters, commit, () => {})
      .then((response) => unwrapEnvelope(response));
  },
  fetchApprovedConsumBatch({ commit }, uuid) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_APPROVED_CONSUM_BATCH, { uuid });
    return api.fetchFull(url, {}, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  validateConsumExport({ commit }, payload) {
    return api.post(endpoints.ERP_LEGACY_CONSUMS_VALIDATE, payload, commit, () => {}).then((response) => {
      return response?.data || response || {};
    });
  },
  exportConsumToErp({ commit }, payload) {
    return api.post(endpoints.ERP_LEGACY_CONSUMS_EXPORT, payload, commit, () => {}).then((response) => {
      return response?.data || response || {};
    });
  },
  retryConsumInvoice({ commit }, uuid) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_INVOICE_RETRY, { uuid });
    return api.post(url, {}, commit, () => {}).then((response) => response?.data || response);
  },
  fetchConsumInvoice({ commit }, uuid) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_INVOICE, { uuid });
    return api.fetchFull(url, {}, commit, () => {}).then((response) => response?.data || response);
  },
  fetchWorkInvoices({ commit }, filters) {
    return api.fetchFull(endpoints.ERP_LEGACY_CONSUMS_WORK_INVOICES, filters, commit, () => {})
      .then(unwrapEnvelope);
  },
  fetchConsumQueue({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_CONSUMS_QUEUE, filters, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination || null;
      commit("SET_CONSUM_QUEUE", { data, pagination });
      return { data, pagination };
    });
  },
  fetchConsumQueueItem({ commit }, uuidOrOptions) {
    const { uuid, setSelected = true } =
      typeof uuidOrOptions === "object" && uuidOrOptions
        ? uuidOrOptions
        : { uuid: uuidOrOptions };
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_QUEUE_ITEM, { uuid });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      if (setSelected) {
        commit("SET_CONSUM_QUEUE_ITEM", payload);
      }
      return payload;
    });
  },
  validateConsumQueueItem({ commit }, { uuid, strictMode = false }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_QUEUE_VALIDATE, { uuid });

    return api.post(url, { strict_mode: strictMode }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  sendConsumQueueItem({ commit }, { uuid, force = false }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_QUEUE_SEND, { uuid });

    return api.post(url, { force }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  retryConsumQueueItem({ commit }, { uuid, mode = "SCHEDULE", ...parameters }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_QUEUE_RETRY, { uuid });

    return api.post(url, { mode, ...parameters }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  cancelConsumQueueItem({ commit }, { uuid, reason }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_CONSUMS_QUEUE_CANCEL, { uuid });

    return api.post(url, { reason }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  bulkRetryConsumQueue({ commit }, payload = {}) {
    return api.post(endpoints.ERP_LEGACY_CONSUMS_BULK_RETRY, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  bulkCancelConsumQueue({ commit }, payload = {}) {
    return api.post(endpoints.ERP_LEGACY_CONSUMS_BULK_CANCEL, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  applyErpEntityAutoMatch({ commit }, { code, payload = {} }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_AUTO_MATCH, { entityCode: code });

    return api.post(url, payload, commit, () => {}).then((response) => {
      if (response?.data && !Array.isArray(response.data)) {
        return response.data;
      }

      return response?.data?.data || {};
    });
  },
  previewErpEntityClean({ commit }, { code, payload = {} }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_CLEAN_PREVIEW, { entityCode: code });
    return api.post(url, payload, commit, () => {}).then((response) => {
      if (response?.data && !Array.isArray(response.data)) {
        return response.data;
      }
      return response?.data?.data || {};
    });
  },
  applyErpEntityClean({ commit }, { code, payload = {} }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_CLEAN_APPLY, { entityCode: code });
    return api.post(url, payload, commit, () => {}).then((response) => {
      if (response?.data && !Array.isArray(response.data)) {
        return response.data;
      }
      return response?.data?.data || {};
    });
  },
  fetchErpEntityStaticMappings({ commit }, code) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_STATIC_MAPPINGS, { entityCode: code });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => unwrapResponseData(response));
  },
  storeErpEntityStaticMapping({ commit }, { code, payload }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_STATIC_MAPPINGS, { entityCode: code });

    return api.post(url, payload, commit, () => {}).then((response) => (
      response?.data === null ? null : unwrapResponseObject(response)
    ));
  },
  destroyErpEntityStaticMapping({ commit }, { code, id }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_STATIC_MAPPING_BY_ID, {
      entityCode: code,
      id,
    });

    return api.remove(url, {}, commit, () => {}).then((response) => response?.data || null);
  },
  fetchErpParcelMappings({ commit }, code) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_PARCEL_MAPPINGS, { entityCode: code });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => unwrapResponseData(response));
  },
  storeErpParcelMapping({ commit }, { code, payload }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_PARCEL_MAPPINGS, { entityCode: code });

    return api.post(url, payload, commit, () => {}).then((response) => unwrapResponseObject(response));
  },
  updateErpParcelMapping({ commit }, { code, id, payload }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_PARCEL_MAPPING_BY_ID, {
      entityCode: code,
      id,
    });

    return api.put(url, payload, commit, () => {}).then((response) => unwrapResponseObject(response));
  },
  destroyErpParcelMapping({ commit }, { code, id }) {
    const url = endpoints.replaceParams(endpoints.ERP_ENTITY_PARCEL_MAPPING_BY_ID, {
      entityCode: code,
      id,
    });

    return api.remove(url, {}, commit, () => {}).then((response) => response?.data || null);
  },
  fetchReceptionQueue({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE, filters, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination || null;
      commit("SET_RECEPTION_QUEUE", { data, pagination });
      return { data, pagination };
    });
  },
  fetchReceptionQueueItem({ commit }, uuid) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE_ITEM, { uuid });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      commit("SET_RECEPTION_QUEUE_ITEM", payload);
      return payload;
    });
  },
  validateReceptionQueueItem({ commit }, { uuid, strictMode = false }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE_VALIDATE, { uuid });

    return api.post(url, { strict_mode: strictMode }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  sendReceptionQueueItem({ commit }, { uuid, force = false }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE_SEND, { uuid });

    return api.post(url, { force }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  retryReceptionQueueItem({ commit }, { uuid, mode = "SCHEDULE" }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE_RETRY, { uuid });

    return api.post(url, { mode }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  cancelReceptionQueueItem({ commit }, { uuid, reason }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_RECEPTIONS_QUEUE_CANCEL, { uuid });

    return api.post(url, { reason }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  bulkRetryReceptionQueue({ commit }, payload = {}) {
    return api.post(endpoints.ERP_LEGACY_RECEPTIONS_BULK_RETRY, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  previewReceptionQueueItem({ commit }, params = {}) {
    const body = {
      source_type: params.source_type,
      source_uuid: params.source_uuid,
    };

    if (params.wm_tip_document) {
      body.wm_tip_document = params.wm_tip_document;
    }

    if (params.payload_json) {
      body.payload_json = params.payload_json;
    }

    return api.post(endpoints.ERP_LEGACY_RECEPTIONS_PREVIEW, body, commit, () => {}).then((response) => {
      const preview = unwrapEnvelope(response);
      commit("SET_RECEPTION_PREVIEW", preview);
      return preview;
    });
  },
  fetchReceptionMappings({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_RECEPTIONS_MAPPINGS, filters, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination || null;
      commit("SET_RECEPTION_MAPPINGS", { data, pagination });
      return { data, pagination };
    });
  },
  storeReceptionMapping({ commit }, payload) {
    return api.post(endpoints.ERP_LEGACY_RECEPTIONS_MAPPINGS, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  updateReceptionMapping({ commit }, { id, payload }) {
    const url = `${endpoints.ERP_LEGACY_RECEPTIONS_MAPPINGS}/${id}`;
    return api.put(url, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  destroyReceptionMapping({ commit }, id) {
    const url = `${endpoints.ERP_LEGACY_RECEPTIONS_MAPPINGS}/${id}`;
    return api.remove(url, {}, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  fetchReceptionSettings({ commit }) {
    return api.fetchFull(endpoints.ERP_LEGACY_RECEPTIONS_SETTINGS, {}, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      commit("SET_RECEPTION_SETTINGS", payload);
      return payload;
    });
  },
  updateReceptionSettings({ commit }, payload) {
    return api.put(endpoints.ERP_LEGACY_RECEPTIONS_SETTINGS, payload, commit, () => {}).then((response) => {
      const data = unwrapEnvelope(response);
      commit("SET_RECEPTION_SETTINGS", data);
      return data;
    });
  },
  fetchTransferQueue({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_TRANSFERS_QUEUE, filters, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination || null;
      commit("SET_TRANSFER_QUEUE", { data, pagination });
      return { data, pagination };
    });
  },
  fetchTransferQueueItem({ commit }, uuidOrOptions) {
    const { uuid, setSelected = true } =
      typeof uuidOrOptions === "object" && uuidOrOptions
        ? uuidOrOptions
        : { uuid: uuidOrOptions };
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_TRANSFERS_QUEUE_ITEM, { uuid });

    return api.fetchFull(url, {}, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      if (setSelected) {
        commit("SET_TRANSFER_QUEUE_ITEM", payload);
      }
      return payload;
    });
  },
  validateTransferQueueItem({ commit }, { uuid, strictMode = false }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_TRANSFERS_QUEUE_VALIDATE, { uuid });

    return api.post(url, { strict_mode: strictMode }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  sendTransferQueueItem({ commit }, { uuid, force = false, cod_subunitate = null, codSubunitate = null, default_carnet_transfer = "", transfer_carnet_symbol = "", payload_json = null }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_TRANSFERS_QUEUE_SEND, { uuid });

    const body = { force };
    const resolvedCodSubunitate = cod_subunitate ?? codSubunitate;

    if (resolvedCodSubunitate !== null && resolvedCodSubunitate !== undefined && resolvedCodSubunitate !== "") {
      body.cod_subunitate = resolvedCodSubunitate;
      body.CodSubunitate = resolvedCodSubunitate;
    }

    const resolvedCarnetSymbol = default_carnet_transfer || transfer_carnet_symbol;
    if (resolvedCarnetSymbol) {
      body.default_carnet_transfer = resolvedCarnetSymbol;
      body.transfer_carnet_symbol = resolvedCarnetSymbol;
      body.SimbolCarnet = resolvedCarnetSymbol;
    }

    if (payload_json) {
      body.payload_json = payload_json;
    }

    return api.post(url, body, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  retryTransferQueueItem({ commit }, { uuid, mode = "SCHEDULE", cod_subunitate = null, codSubunitate = null, default_carnet_transfer = "", transfer_carnet_symbol = "", payload_json = null }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_TRANSFERS_QUEUE_RETRY, { uuid });

    const body = { mode };
    const resolvedCodSubunitate = cod_subunitate ?? codSubunitate;

    if (resolvedCodSubunitate !== null && resolvedCodSubunitate !== undefined && resolvedCodSubunitate !== "") {
      body.cod_subunitate = resolvedCodSubunitate;
      body.CodSubunitate = resolvedCodSubunitate;
    }

    const resolvedCarnetSymbol = default_carnet_transfer || transfer_carnet_symbol;
    if (resolvedCarnetSymbol) {
      body.default_carnet_transfer = resolvedCarnetSymbol;
      body.transfer_carnet_symbol = resolvedCarnetSymbol;
      body.SimbolCarnet = resolvedCarnetSymbol;
    }

    if (payload_json) {
      body.payload_json = payload_json;
    }

    return api.post(url, body, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  cancelTransferQueueItem({ commit }, { uuid, reason }) {
    const url = endpoints.replaceParams(endpoints.ERP_LEGACY_TRANSFERS_QUEUE_CANCEL, { uuid });

    return api.post(url, { reason }, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  bulkRetryTransferQueue({ commit }, payload = {}) {
    return api.post(endpoints.ERP_LEGACY_TRANSFERS_BULK_RETRY, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  previewTransferQueueItem({ commit }, params = {}) {
    const body = {
      source_type: params.source_type || "SM_TRANSFER",
      source_uuid: params.source_uuid,
    };

    if (params.payload_json) {
      body.payload_json = params.payload_json;
    }

    const resolvedCodSubunitate = params.cod_subunitate ?? params.codSubunitate;
    if (resolvedCodSubunitate !== null && resolvedCodSubunitate !== undefined && resolvedCodSubunitate !== "") {
      body.cod_subunitate = resolvedCodSubunitate;
      body.CodSubunitate = resolvedCodSubunitate;
    }

    const resolvedCarnetSymbol = params.default_carnet_transfer || params.transfer_carnet_symbol;
    if (resolvedCarnetSymbol) {
      body.default_carnet_transfer = resolvedCarnetSymbol;
      body.transfer_carnet_symbol = resolvedCarnetSymbol;
      body.SimbolCarnet = resolvedCarnetSymbol;
    }

    return api.post(endpoints.ERP_LEGACY_TRANSFERS_PREVIEW, body, commit, () => {}).then((response) => {
      const preview = unwrapEnvelope(response);
      commit("SET_TRANSFER_PREVIEW", preview);
      return preview;
    });
  },
  fetchTransferMappings({ commit }, filters = {}) {
    return api.fetchFull(endpoints.ERP_LEGACY_TRANSFERS_MAPPINGS, filters, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination || null;
      commit("SET_TRANSFER_MAPPINGS", { data, pagination });
      return { data, pagination };
    });
  },
  storeTransferMapping({ commit }, payload) {
    return api.post(endpoints.ERP_LEGACY_TRANSFERS_MAPPINGS, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  updateTransferMapping({ commit }, { id, payload }) {
    const url = `${endpoints.ERP_LEGACY_TRANSFERS_MAPPINGS}/${id}`;
    return api.put(url, payload, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  destroyTransferMapping({ commit }, id) {
    const url = `${endpoints.ERP_LEGACY_TRANSFERS_MAPPINGS}/${id}`;
    return api.remove(url, {}, commit, () => {}).then((response) => unwrapEnvelope(response));
  },
  fetchTransferSettings({ commit }) {
    return api.fetchFull(endpoints.ERP_LEGACY_TRANSFERS_SETTINGS, {}, commit, () => {}).then((response) => {
      const payload = unwrapEnvelope(response);
      commit("SET_TRANSFER_SETTINGS", payload);
      return payload;
    });
  },
  updateTransferSettings({ commit }, payload) {
    return api.put(endpoints.ERP_LEGACY_TRANSFERS_SETTINGS, payload, commit, () => {}).then((response) => {
      const data = unwrapEnvelope(response);
      commit("SET_TRANSFER_SETTINGS", data);
      return data;
    });
  },
};

export { state, mutations, getters, actions };
